import type { ActivityTypeEnum, JobStatusEnum, NotificationTypeEnum, WorkflowActivityStatusEnum } from "@/graphql.ts";

// --- Common ---/
export enum SubscriptionMessageTypeEnum {
  Fail = "subscription_fail",
  End = "subscription_end",
  Data = "subscription_data",
  Start = "subscription_start",
  Success = "subscription_success",
  KeepAlive = "subscription_keepalive",
}

interface CommonSubscriptionResponseDef {
  id: string;
  messageType: SubscriptionMessageTypeEnum;
}

// --- Subscription ---
export enum SubscriptionTypeEnum {
  Alert = "alert",
  FileDownload = "cookie",
  FileScanner = "filescanner",
  File = "file",
  Job = "job",
  WorkflowActivity = "workflow_activity",
  Notification = "notification",
  Project = "project",
}

export interface AlertSubscription {
  eventType: SubscriptionTypeEnum.Alert;
  variables?: undefined;
}

export interface FileDownloadSubscription {
  eventType: SubscriptionTypeEnum.FileDownload;
  variables?: undefined;
}

export interface FileScannerSubscription {
  eventType: SubscriptionTypeEnum.FileScanner;
  variables?: undefined;
}

export interface FileSubscription {
  eventType: SubscriptionTypeEnum.File;
  variables?: undefined;
}

export interface JobSubscription {
  eventType: SubscriptionTypeEnum.Job;
  variables?: {
    jobId: number;
  };
}

export interface WorkflowActivitySubscription {
  eventType: SubscriptionTypeEnum.WorkflowActivity;
  variables?: {
    workflowEventId: number;
  };
}

export interface NotificationSubscription {
  eventType: SubscriptionTypeEnum.Notification;
  variables?: undefined;
}

export interface ProjectSubscription {
  eventType: SubscriptionTypeEnum.Project;
  variables?: {
    projectId: number;
  };
}

export type AllSubscriptions =
  | AlertSubscription
  | FileDownloadSubscription
  | FileScannerSubscription
  | FileSubscription
  | JobSubscription
  | WorkflowActivitySubscription
  | NotificationSubscription
  | ProjectSubscription;

export type SubscriptionData<T extends AllSubscriptions> = CommonSubscriptionResponseDef & {
  eventType: T["eventType"];
  variables: T["variables"];
  token?: string;
};

export type UnSubscriptionData = CommonSubscriptionResponseDef & {
  token?: string;
};

// --- Response Events --- //
export interface SubscriptionEvent<T extends AllSubscriptions> {
  /** MT Server internals */
  id: number;
  timestamp: number;
  type: T["eventType"];
}

export interface AlertEvent extends SubscriptionEvent<AlertSubscription> {
  message: string;
  exception: { stackTrace: string }; // Hard to be safe about this one
}

export enum FileDownloadStatus {
  DIRECT_DOWNLOAD = "DIRECT_DOWNLOAD",
  PREPARING = "PREPARING",
  READY = "READY",
  INVALID = "INVALID",
  ERROR = "ERROR",
}

export interface FileDownloadEvent extends SubscriptionEvent<FileDownloadSubscription> {
  name: string;
  status: FileDownloadStatus; // in case of error (download could not be started)
  message: string; // in case of error (download could not be started)
  fileKey: string;
  fileName: string;
}

export enum FileScannerStatus {
  STALE_SCAN_SUCCESS = "STALESCAN_SUCCESS",
  STALE_SCAN_UPDATED = "STALESCAN_UPDATED",
  STALE_SCAN_FAILED = "STALESCAN_FAILED",
  DELETION_SUCCESS = "DELETION_SUCCESS",
  DELETION_WARNING = "DELETION_WARNING",
  DELETION_FAILED = "DELETION_FAILED",
}

export interface FileScannerEvent extends SubscriptionEvent<FileScannerSubscription> {
  status: FileScannerStatus;
  message: string;
}

export enum FileStatus {
  Success = "SUCCESS",
  Failed = "FAILED",
  Error = "ERROR",
  Info = "INFO",
  Exists = "EXISTS",
  Indexing = "INDEXING",
}

export interface FileEvent extends SubscriptionEvent<FileSubscription> {
  message: string;
  fileId: number;
  status: FileStatus; // TODO This is the type? (was "type" before)
  // receiver: string; //! Do not use
}

export enum JobEventTypeEnum {
  Blast = "BLAST",
  Workflow = "WORKFLOW",
  Other = "OTHER",
}

export interface JobEvent extends SubscriptionEvent<JobSubscription> {
  jobId: number;
  status: JobStatusEnum;
  name: string;
  jobType: JobEventTypeEnum;
}

// TODO We need to extend here to also have the "workflow" update
export interface WorkflowActivityEvent extends SubscriptionEvent<WorkflowActivitySubscription> {
  workflowEventId: number;
  activity?: {
    identifier: string;
    status: WorkflowActivityStatusEnum;
  };
  workflow?: {
    status: JobStatusEnum;
    /** Given only on terminated statuses */
    expires?: string;
    /** Who is currently running the session. As it can be opened by multiple people at the same time. */
    user: string;
  };
}

export interface NotificationEvent extends SubscriptionEvent<NotificationSubscription> {
  notificationId: number;
  notificationType: NotificationTypeEnum;
  // email: boolean; //! Do not use
}

export interface ProjectEvent extends SubscriptionEvent<ProjectSubscription> {
  activityId: number;
  activityType: ActivityTypeEnum;
  projectId: number;
}

type AllEvents =
  | AlertEvent
  | FileScannerEvent
  | FileDownloadEvent
  | FileEvent
  | JobEvent
  | WorkflowActivityEvent
  | NotificationEvent
  | ProjectEvent;

// --- Response --- //
// export type ResponseData<S extends AllSubscription> = S extends { eventType: infer E }
//   ? CommonSubscriptionResponseDef & {
//       event: Extract<AllEvents, { type: E }>;
//     }
//   : never;

type ResponseEvent<S extends AllSubscriptions> = Extract<AllEvents, { type: S["eventType"] }>;

export type ResponseData<S extends AllSubscriptions> = CommonSubscriptionResponseDef & {
  event: ResponseEvent<S>;
  errors: Error | Error[];
};

export type ResponseDataEvent<S extends AllSubscriptions> = ResponseData<S>["event"];
